import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr, money } from "@/lib/api";
import { Button, Input } from "@/lib/ui";
import PaymentModal from "@/components/PaymentModal";

export default function SellerCRM() {
  const [info, setInfo] = useState(null);
  const [labels, setLabels] = useState([]);
  const [leads, setLeads] = useState([]);
  const [order, setOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [company] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editLabel, setEditLabel] = useState(null);
  const [tplStage, setTplStage] = useState("");
  const [tplText, setTplText] = useState("");
  const [noteEdit, setNoteEdit] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragCard, setDragCard] = useState(null);

  const loadInfo = useCallback(() => api.get("/seller/crm/info").then(({ data }) => setInfo(data)).catch(() => {}), []);
  const loadBoard = useCallback(async () => {
    try {
      const { data } = await api.get("/seller/crm/leads");
      setLabels(data.labels); setLeads(data.leads);
    } catch (e) { /* not subscribed */ }
  }, []);

  useEffect(() => { loadInfo(); }, [loadInfo]);
  useEffect(() => { if (info?.subscribed) loadBoard(); }, [info, loadBoard]);
  useEffect(() => { if (!tplStage && labels.length) setTplStage(labels[0].id); }, [labels, tplStage]);
  useEffect(() => { const lb = labels.find((l) => l.id === tplStage); setTplText(lb?.message_template || ""); }, [tplStage, labels]);

  if (!info) return <p className="text-slate-400">Loading…</p>;

  // ---------- Locked (not subscribed) ----------
  if (!info.subscribed) {
    const pkg = info.package;
    const price = pkg?.amount || 2500;
    return (
      <div>
        <h1 className="font-head text-2xl font-extrabold text-slate-900">Leads CRM</h1>
        <div className="mt-5 overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-8 text-center" data-testid="crm-locked">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-600 text-white shadow-lg">
            <i className="fa-solid fa-lock text-2xl" />
          </div>
          <p className="font-head text-3xl font-extrabold text-slate-900" data-testid="crm-conn-count">
            {info.connections_count} buyer{info.connections_count === 1 ? "" : "s"} connected
          </p>
          <p className="mt-1 text-slate-500">
            {info.connections_count > 0
              ? "These buyers tapped WhatsApp on your stock. Subscribe to see their numbers and follow up to close deals."
              : "Buyers who tap WhatsApp on your stock appear here. Subscribe to capture every lead and follow up to close more deals."}
          </p>
          <ul className="mx-auto mt-5 max-w-md space-y-2 text-left text-sm text-slate-600">
            {["See every buyer's mobile number who contacted you",
              "One-tap WhatsApp follow-up with saved message templates",
              "Kanban board with your own stages to track & close deals",
              "Notes and follow-up reminders on every lead"].map((f) => (
              <li key={f} className="flex items-start gap-2"><i className="fa-solid fa-circle-check mt-0.5 text-emerald-500" />{f}</li>
            ))}
          </ul>
          <p className="mt-6 font-head text-4xl font-extrabold text-orange-600">{money(price)}<span className="text-base font-medium text-slate-400"> + GST / year</span></p>
          <Button className="mt-4 px-8 py-3" data-testid="crm-subscribe-btn"
            onClick={() => setOrder({ type: "crm", plan_name: pkg?.name || "Leads CRM", amount: price })}>
            <i className="fa-solid fa-unlock" /> Subscribe & Unlock
          </Button>
          <p className="mt-2 text-xs text-slate-400">Upload your payment screenshot — admin activates within 48 hours.</p>
        </div>
        {order && <PaymentModal order={order} onClose={() => setOrder(null)} onDone={loadInfo} />}
      </div>
    );
  }

  // ---------- Subscribed ----------
  const waHref = (lead) => {
    const lb = labels.find((x) => x.id === (lead.label_id || ""));
    const tpl = lb?.message_template || "";
    if (!tpl) return `https://wa.me/91${lead.phone}`;
    const msg = tpl
      .replace(/\{name\}/gi, lead.phone)
      .replace(/\{product\}/gi, lead.last_product || "the stock you enquired about")
      .replace(/\{company\}/gi, info.plan ? (info.company || "us") : "us");
    return `https://wa.me/91${lead.phone}?text=${encodeURIComponent(msg)}`;
  };

  const moveLead = async (lead, labelId) => {
    setLeads((ls) => ls.map((l) => (l.phone === lead.phone ? { ...l, label_id: labelId } : l)));
    try { await api.put(`/seller/crm/leads/${lead.phone}`, { label_id: labelId }); }
    catch (e) { toast.error(apiErr(e)); loadBoard(); }
  };
  const addLabel = async () => {
    if (!newLabel.trim()) return;
    try { await api.post("/seller/crm/labels", { name: newLabel.trim(), message_template: "" }); setNewLabel(""); loadBoard(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveLabel = async () => {
    const lb = labels.find((x) => x.id === editLabel.id);
    try { await api.put(`/seller/crm/labels/${editLabel.id}`, { name: editLabel.name.trim(), message_template: lb?.message_template || "" }); setEditLabel(null); loadBoard(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delLabel = async (id) => {
    if (!window.confirm("Delete this stage? Its leads move to Unlabeled.")) return;
    try { await api.delete(`/seller/crm/labels/${id}`); loadBoard(); } catch (e) { toast.error(apiErr(e)); }
  };
  const savePlanner = async () => {
    const lb = labels.find((l) => l.id === tplStage);
    if (!lb) return;
    try { await api.put(`/seller/crm/labels/${lb.id}`, { name: lb.name, message_template: tplText }); toast.success(`Message for "${lb.name}" saved`); loadBoard(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveNote = async () => {
    try {
      await api.put(`/seller/crm/leads/${noteEdit.phone}`, { note: noteEdit.note, followup_date: noteEdit.followup_date || "" });
      setLeads((ls) => ls.map((l) => (l.phone === noteEdit.phone ? { ...l, note: noteEdit.note, followup_date: noteEdit.followup_date } : l)));
      setNoteEdit(null); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const dropLabel = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = labels.map((l) => l.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setLabels(ids.map((id) => labels.find((l) => l.id === id)));
    setDragId(null);
    try { await api.post("/seller/crm/labels/reorder", { ids }); } catch (e) { toast.error(apiErr(e)); loadBoard(); }
  };

  const visible = leads.filter((l) => {
    const s = search.trim().toLowerCase();
    return !s || [l.phone, l.last_product, l.last_city].some((v) => (v || "").toLowerCase().includes(s));
  });
  const columns = [...labels, { id: "", name: "Unlabeled", unlabeled: true }];
  const leadsOf = (id) => visible.filter((l) => (l.label_id || "") === id);
  const todayStr = new Date().toLocaleDateString("en-CA");
  const due = leads.filter((l) => l.followup_date && l.followup_date <= todayStr).sort((a, b) => a.followup_date.localeCompare(b.followup_date));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-head text-2xl font-extrabold text-slate-900">Leads CRM</h1>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="crm-active-badge">
          <i className="fa-solid fa-circle-check" /> Active{info.expiry ? ` · expires ${new Date(info.expiry).toLocaleDateString()}` : ""}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-500">Buyers who tapped WhatsApp on your stock. Follow up and move them across your stages to close deals.</p>

      {/* Message template planner */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="crm-msg-planner">
        <p className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><i className="fa-brands fa-whatsapp text-emerald-500" /> Message Template Planner</p>
        <p className="mb-3 text-xs text-slate-400">Plan the WhatsApp message for each stage. Placeholders: <code className="rounded bg-slate-100 px-1">{"{product}"}</code>.</p>
        <div className="grid gap-3 sm:grid-cols-[200px_1fr]">
          <select value={tplStage} onChange={(e) => setTplStage(e.target.value)} data-testid="crm-planner-stage" className="h-10 rounded-lg border border-slate-300 px-3 text-sm">
            {labels.map((lb) => <option key={lb.id} value={lb.id}>{lb.name}</option>)}
          </select>
          <textarea rows={3} value={tplText} onChange={(e) => setTplText(e.target.value)} data-testid="crm-planner-text" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="e.g. Hi, following up on {product}…" />
        </div>
        <div className="mt-3 flex justify-end"><Button onClick={savePlanner} data-testid="crm-planner-save"><i className="fa-solid fa-check" /> Save Message</Button></div>
      </div>

      {/* Label planner */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Stages</p>
        <div className="flex flex-wrap items-center gap-2">
          {labels.map((lb) => (
            <span key={lb.id} draggable={editLabel?.id !== lb.id} onDragStart={() => setDragId(lb.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropLabel(lb.id)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${dragId === lb.id ? "border-orange-400 bg-orange-50 opacity-60" : "border-slate-200 bg-slate-50"}`}>
              {editLabel?.id === lb.id ? (
                <>
                  <input autoFocus value={editLabel.name} onChange={(e) => setEditLabel((s) => ({ ...s, name: e.target.value }))} className="w-24 rounded border border-slate-300 px-1 text-sm" />
                  <button onClick={saveLabel} className="text-emerald-600"><i className="fa-solid fa-check" /></button>
                  <button onClick={() => setEditLabel(null)} className="text-slate-400"><i className="fa-solid fa-xmark" /></button>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-grip-vertical cursor-grab text-slate-300" data-testid={`crm-label-drag-${lb.id}`} />
                  <span className="font-semibold text-slate-700">{lb.name}</span>
                  <button onClick={() => setEditLabel({ id: lb.id, name: lb.name })} data-testid={`crm-label-edit-${lb.id}`} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-pen text-xs" /></button>
                  <button onClick={() => delLabel(lb.id)} data-testid={`crm-label-del-${lb.id}`} className="text-slate-300 hover:text-red-500"><i className="fa-solid fa-trash text-xs" /></button>
                </>
              )}
            </span>
          ))}
          <Input className="w-40" placeholder="New stage…" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLabel()} data-testid="crm-new-label" />
          <Button variant="outline" onClick={addLabel} data-testid="crm-add-label"><i className="fa-solid fa-plus" /> Add</Button>
        </div>
      </div>

      {/* Search */}
      <div className="mt-4"><Input className="max-w-xs" placeholder="Search mobile / product / city…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="crm-search" /></div>

      {/* Follow-ups due */}
      {due.length > 0 && (
        <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-4" data-testid="crm-followups">
          <p className="mb-2 font-head font-bold text-orange-800"><i className="fa-solid fa-bell mr-1" />Follow-ups due ({due.length})</p>
          <div className="flex flex-wrap gap-2">
            {due.map((l) => (
              <div key={l.phone} className="flex items-center gap-3 rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm shadow-sm">
                <div><p className="font-semibold text-slate-800">+91 {l.phone}</p><p className={`text-xs font-semibold ${l.followup_date < todayStr ? "text-red-600" : "text-orange-600"}`}>{l.followup_date < todayStr ? "Overdue" : "Today"} · {l.followup_date}</p></div>
                <a href={waHref(l)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white"><i className="fa-brands fa-whatsapp" /> WhatsApp</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Board */}
      <div className="mt-5 flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => {
          const items = leadsOf(col.id);
          return (
            <div key={col.id || "unlabeled"}
              onDragOver={(e) => { if (dragCard || !col.unlabeled) e.preventDefault(); }}
              onDrop={() => {
                if (dragCard) { const lead = leads.find((l) => l.phone === dragCard); if (lead && (lead.label_id || "") !== (col.id || "")) moveLead(lead, col.id); setDragCard(null); }
                else if (!col.unlabeled) dropLabel(col.id);
              }}
              className={`w-72 flex-shrink-0 rounded-2xl p-3 transition ${dragId === col.id ? "bg-orange-100 ring-2 ring-orange-300" : dragCard ? "bg-slate-100 ring-2 ring-dashed ring-orange-200" : "bg-slate-100"}`} data-testid={`crm-col-${col.id || "unlabeled"}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="flex items-center gap-2 font-head font-bold text-slate-800">
                  {!col.unlabeled && <i draggable onDragStart={() => setDragId(col.id)} onDragEnd={() => setDragId(null)} className="fa-solid fa-grip-vertical cursor-grab text-slate-400" data-testid={`crm-col-drag-${col.id}`} />}
                  {col.name}
                </span>
                <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((lead) => (
                  <div key={lead.phone} draggable onDragStart={(e) => { e.stopPropagation(); setDragCard(lead.phone); }} onDragEnd={() => setDragCard(null)}
                    className={`cursor-grab rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition ${dragCard === lead.phone ? "opacity-40" : ""}`} data-testid={`crm-card-${lead.phone}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800">+91 {lead.phone}</p>
                        {lead.last_product && <p className="mt-0.5 truncate text-xs text-slate-400">↳ {lead.last_product}{lead.last_city ? ` · ${lead.last_city}` : ""}</p>}
                        <p className="text-xs text-slate-400"><i className="fa-solid fa-hand-pointer mr-1" />{lead.clicks} click{lead.clicks === 1 ? "" : "s"}</p>
                        {lead.note && <p className="mt-1 flex items-start gap-1 text-xs text-slate-600"><i className="fa-regular fa-note-sticky mt-0.5 text-slate-400" />{lead.note}</p>}
                        {lead.followup_date && <p className="mt-0.5 text-xs font-semibold text-orange-600"><i className="fa-regular fa-calendar mr-1" />{lead.followup_date}</p>}
                      </div>
                      <button onClick={() => setNoteEdit({ phone: lead.phone, note: lead.note || "", followup_date: lead.followup_date || "" })} data-testid={`crm-note-${lead.phone}`} className="text-slate-300 hover:text-orange-500"><i className="fa-solid fa-pen text-xs" /></button>
                    </div>
                    <a href={waHref(lead)} target="_blank" rel="noreferrer" data-testid={`crm-wa-${lead.phone}`}
                      className="mt-2 flex items-center justify-center gap-1 rounded-lg bg-emerald-500 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600">
                      <i className="fa-brands fa-whatsapp" /> WhatsApp Follow-up
                    </a>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {labels.filter((l) => l.id !== (lead.label_id || "")).map((l) => (
                        <button key={l.id} onClick={() => moveLead(lead, l.id)} data-testid={`crm-move-${lead.phone}-${l.id}`}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-orange-100 hover:text-orange-700">→ {l.name}</button>
                      ))}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="px-1 py-4 text-center text-xs text-slate-400">No leads</p>}
              </div>
            </div>
          );
        })}
      </div>

      {noteEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="crm-note-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-slate-900">Note & Follow-up</h3>
              <button onClick={() => setNoteEdit(null)} className="text-slate-400"><i className="fa-solid fa-xmark" /></button>
            </div>
            <textarea rows={3} value={noteEdit.note} onChange={(e) => setNoteEdit((s) => ({ ...s, note: e.target.value }))} data-testid="crm-note-text" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Quick note…" />
            <input type="date" value={noteEdit.followup_date || ""} onChange={(e) => setNoteEdit((s) => ({ ...s, followup_date: e.target.value }))} data-testid="crm-note-date" className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNoteEdit(null)}>Cancel</Button>
              <Button onClick={saveNote} data-testid="crm-note-save"><i className="fa-solid fa-check" /> Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
